import React, { useEffect, useRef, useCallback } from 'react';
import { IoVideocamOutline } from "react-icons/io5";
import { AiOutlineDownload } from "react-icons/ai";
import { PiArrowBendRightDownBold } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import { getDisplayStyle } from './TagConfig';

const ImageBento = ({ images, currentPage, imagesPerPage, totalImages, handleLoadMore }) => {
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const resizeTimeoutRef = useRef(null);

  const resizeGridItem = useCallback((item) => {
    try {
      if (!gridRef.current) return;
      
      const rowHeight = parseInt(window.getComputedStyle(gridRef.current).getPropertyValue('grid-auto-rows'));
      const rowGap = parseInt(window.getComputedStyle(gridRef.current).getPropertyValue('gap'));
      const contentEl = item.querySelector('.content');
      
      if (!contentEl) return;
      
      const contentHeight = contentEl.getBoundingClientRect().height;
      const rowSpan = Math.ceil((contentHeight + rowGap) / (rowHeight + rowGap));
      item.style.gridRowEnd = `span ${rowSpan}`;
    } catch (error) {
      console.error('Error resizing grid item:', error);
    }
  }, []);

  const resizeAllGridItems = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      try {
        const allItems = document.getElementsByClassName("bento-grid-item");
        for (let x = 0; x < allItems.length; x++) {
          resizeGridItem(allItems[x]);
        }
      } catch (error) {
        console.error('Error resizing all grid items:', error);
      }
    }, 100);
  }, [resizeGridItem]);

  const handleImageLoad = useCallback((event) => {
    const gridItem = event.target.closest('.bento-grid-item');
    if (gridItem) {
      resizeGridItem(gridItem);
      // Also resize all items to ensure proper layout
      resizeAllGridItems();
    }
  }, [resizeGridItem, resizeAllGridItems]);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      // Debounce the resize observer callback
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        resizeAllGridItems();
      }, 100);
    });

    if (gridRef.current) {
      observer.observe(gridRef.current);
      resizeAllGridItems();
    }

    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(resizeAllGridItems, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, [resizeAllGridItems]);

  // Run resizeAllGridItems when images change
  useEffect(() => {
    resizeAllGridItems();
  }, [images, resizeAllGridItems]);

  return (
    <div className="w-full max-w-7xl mx-0 px-4 sm:px-6 lg:px-8 pb-32">
      <div 
        ref={gridRef} 
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 auto-rows-[10px]"
      >
        {images.length === 0 ? (
          <div className="col-span-full flex flex-col items-center justify-center min-h-[60vh] text-black">
            <p className="text-3xl font-medium mb-4">Enter your prompt below!</p>
            <PiArrowBendRightDownBold className="w-12 h-12" />
          </div>
        ) : (
          images.map((image) => (
            <div key={image.jobId} className="bento-grid-item">
              <div className="content">
                {image.status === 'Completed' && image.image_url ? (
                  <div className="relative group rounded-xl overflow-hidden bg-white shadow-sm transition duration-300 ease-in-out hover:shadow-lg">
                    <img
                      src={image.image_url}
                      alt={image.inputPrompt}
                      className="w-full h-auto"
                      loading="lazy"
                      onLoad={handleImageLoad}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300">
                      <div className="absolute top-3 right-3 flex gap-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div 
                          onClick={() => navigate('/dashboard/videos', { 
                            state: { 
                              imageUrl: image.image_url,
                              isImageMode: true
                            }
                          })}
                          className="relative bg-white/10 backdrop-blur-md p-1.5 rounded-full cursor-pointer hover:bg-white/20 transition-all duration-300 border border-white/20 group/btn"
                        >
                          <IoVideocamOutline className="w-3.5 h-3.5 text-white" />
                          <div className="absolute right-0 top-full mt-2 w-max opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200">
                            <div className="bg-gray-900 text-white text-xs py-1 px-2 rounded-md whitespace-nowrap">
                              Generate video with image
                            </div>
                          </div>
                        </div>
                        <div 
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = image.image_url;
                            link.download = `${image.inputPrompt.slice(0, 30)}.png`;
                            link.target = '_blank';
                            link.rel = 'noopener noreferrer';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          className="relative bg-white/10 backdrop-blur-md p-1.5 rounded-full cursor-pointer hover:bg-white/20 transition-all duration-300 border border-white/20 group/download"
                        >
                          <AiOutlineDownload className="w-3.5 h-3.5 text-white" />
                          <div className="absolute right-0 top-full mt-2 w-max opacity-0 group-hover/download:opacity-100 transition-opacity duration-200">
                            <div className="bg-gray-900 text-white text-xs py-1 px-2 rounded-md whitespace-nowrap">
                              Download image
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <p className="text-sm text-white font-medium line-clamp-2">
                          {image.inputPrompt}
                        </p>
                        {image.style && getDisplayStyle(image.style) && (
                          <span className="inline-block mt-2 px-2 py-0.5 text-[11px] font-medium bg-white/20 text-white rounded-full">
                            {getDisplayStyle(image.style)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative rounded-xl overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 aspect-square">
                    {image.status === 'Processing' ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="relative w-24 h-24">
                          <div className="absolute inset-0 rounded-full border-4 border-gray-200"></div>
                          <div className="absolute inset-0 rounded-full border-4 border-black border-t-transparent animate-spin"></div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <svg className="w-8 h-8 text-black" fill="none" viewBox="0 0 24 24">
                              <path
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="mt-6 space-y-2 text-center">
                          <p className="text-base font-medium text-gray-900">Creating your image</p>
                          <p className="text-sm text-gray-500">This usually takes 10-15 seconds</p>
                        </div>
                      </div>
                    ) : image.status === 'Failed' ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                        <p className="text-sm font-medium text-gray-900 text-center">
                          There was an error generating your image. Please try again.
                        </p>
                      </div>
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                        <div className="w-12 h-12 rounded-full bg-blue-100 mb-4 flex items-center justify-center">
                          <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <p className="text-sm font-medium text-gray-900">Processing image...</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      {totalImages > currentPage * imagesPerPage && (
        <div className="flex justify-center mt-8 mb-4">
          <button
            onClick={handleLoadMore}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 rounded-lg border border-gray-200 shadow-sm transition-colors"
          >
            Load More
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageBento; 